// FoodPicker.js — 음식 고르기 창 (식단 등록 · 음식풀 추가 · 잘어울림 공용)
//   single: 음식을 고르면 같은 창 아래에서 섭취량(g) 입력 → onPick(food, intake)
//   multi : 최대 max 개까지 칩으로 고름 → onApply(foods)
import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import FoodRow from './FoodRow';
import Pagination from './Pagination';
import EmptyState from './EmptyState';
import { MacroText, fmt } from './Nutrients';
import { foodcustomsearch, foodcustomsearchcount, foodsearch, foodsearchcount } from '../../api/dietApi';
import './ui.css';

const LIMIT = 10;
const SOURCES = [
  { key: 'all', label: '전체 음식', list: foodsearch, count: foodsearchcount },
  { key: 'my', label: '내 음식', list: foodcustomsearch, count: foodcustomsearchcount },
];

export default function FoodPicker({
  open, title = '음식 추가', mode = 'single', max = 5, initial = [], confirmLabel = '추가',
  onPick, onApply, onClose,
}) {
  const [source, setSource] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [intake, setIntake] = useState('100');
  const [chips, setChips] = useState(initial);

  useEffect(() => {
    if (!open) return;
    setKeyword(''); setQuery(''); setPage(1); setItems([]); setTotal(0);
    setSelected(null); setIntake('100'); setError(''); setChips(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // 전체 음식은 검색어가 있어야 조회 (16만 건), 내 음식은 검색어 없이도 목록
    if (source === 'all' && !query) { setItems([]); setTotal(0); return; }
    const src = SOURCES.find((s) => s.key === source);
    const params = { foodNm: query };
    setLoading(true); setError('');
    Promise.all([src.list({ ...params, offset: (page - 1) * LIMIT, limit: LIMIT }), src.count(params)])
      .then(([l, c]) => { setItems(Array.isArray(l.data) ? l.data : []); setTotal(Number(c.data) || 0); })
      .catch(() => { setItems([]); setTotal(0); setError('검색 중 오류가 발생했습니다.'); })
      .finally(() => setLoading(false));
  }, [open, source, query, page]);

  const submitSearch = (e) => { e.preventDefault(); setPage(1); setSelected(null); setQuery(keyword.trim()); };

  const g = Number(intake);
  const intakeOk = Number.isFinite(g) && g >= 1;
  const scaled = (k) => ((Number(selected?.[k]) || 0) * (intakeOk ? g : 0)) / 100;

  const toggleChip = (f) => {
    setChips((prev) => prev.some((x) => x.foodCd === f.foodCd)
      ? prev.filter((x) => x.foodCd !== f.foodCd)
      : (prev.length >= max ? prev : [...prev, f]));
  };

  const footer = mode === 'single' ? (
    <>
      <button className="btn ghost" type="button" onClick={onClose}>취소</button>
      <button className="btn primary" type="button" disabled={!selected || !intakeOk} onClick={() => onPick(selected, g)}>{confirmLabel}</button>
    </>
  ) : (
    <>
      <span className="picker-count">선택 {chips.length}/{max}</span>
      <button className="btn ghost" type="button" onClick={onClose}>취소</button>
      <button className="btn primary" type="button" disabled={!chips.length} onClick={() => onApply(chips)}>{confirmLabel}</button>
    </>
  );

  let body;
  if (loading) body = <p className="picker-msg">검색 중…</p>;
  else if (error) body = <p className="picker-msg error">{error}</p>;
  else if (source === 'all' && !query) body = <p className="picker-msg">음식 이름을 입력해 검색하세요.</p>;
  else if (!items.length) body = <EmptyState message={source === 'my' ? '등록한 음식이 없습니다.' : '검색 결과가 없습니다.'} />;
  else {
    body = (
      <>
        {items.map((f) => (
          <FoodRow
            key={f.foodCd}
            food={f}
            meta="100g 기준"
            selected={mode === 'single' ? selected?.foodCd === f.foodCd : chips.some((x) => x.foodCd === f.foodCd)}
            onClick={() => (mode === 'single' ? setSelected(f) : toggleChip(f))}
          />
        ))}
        <Pagination page={page} total={total} pageSize={LIMIT} onChange={setPage} />
      </>
    );
  }

  return (
    <Modal open={open} title={title} size="md" onClose={onClose} footer={footer}>
      <div className="picker-tabs">
        {SOURCES.map((s) => (
          <button key={s.key} type="button" className={source === s.key ? 'on' : ''}
            onClick={() => { setSource(s.key); setPage(1); setSelected(null); }}>{s.label}</button>
        ))}
      </div>
      <form className="picker-search" onSubmit={submitSearch}>
        <input className="input" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="음식 이름 (예: 현미밥)" autoFocus />
        <button className="btn" type="submit">검색</button>
      </form>

      {mode === 'multi' && chips.length > 0 && (
        <div className="picker-chips">
          {chips.map((c) => (
            <button key={c.foodCd} type="button" className="chip" onClick={() => toggleChip(c)}>{c.foodNm} ✕</button>
          ))}
        </div>
      )}

      <div className="picker-list">{body}</div>

      {mode === 'single' && selected && (
        <div className="picker-intake">
          <div className="picker-intake-name">
            <strong>{selected.foodNm}</strong>
            <label>
              섭취량
              <input className="input" type="number" min="1" value={intake} onChange={(e) => setIntake(e.target.value)} />
              g
            </label>
          </div>
          <div className="picker-intake-sum">
            <span>{fmt(scaled('kcal'))} kcal</span>
            <MacroText carbo={scaled('carbo')} protein={scaled('protein')} fat={scaled('fat')} />
          </div>
          {!intakeOk && <p className="picker-msg error">섭취량은 1g 이상 입력해 주세요.</p>}
        </div>
      )}
    </Modal>
  );
}
